import { IPublicClientApplication } from '@azure/msal-browser';
import { graphGet, graphPost, graphPut, graphDelete } from '../api/graphClient';

// ── Site + drive discovery (used by the library picker) ───────────────

export interface SpSite {
  id: string;
  displayName?: string;
  name?: string;
  webUrl: string;
}

export interface SpDrive {
  id: string;
  name: string;
  webUrl: string;
  driveType?: string;
}

/**
 * Search SharePoint sites the user can access. Graph security-trims this
 * automatically, so callers see only sites they have rights to.
 */
export async function searchSites(pca: IPublicClientApplication, query: string): Promise<SpSite[]> {
  const q = encodeURIComponent(query);
  const data = await graphGet<{ value: SpSite[] }>(pca, `/sites?search=${q}`);
  return data.value ?? [];
}

/** List the document libraries (drives) for a site. */
export async function getSiteDrives(pca: IPublicClientApplication, siteId: string): Promise<SpDrive[]> {
  const data = await graphGet<{ value: SpDrive[] }>(
    pca, `/sites/${siteId}/drives?$select=id,name,webUrl,driveType`,
  );
  return data.value ?? [];
}

// ── Drive item browsing ───────────────────────────────────────────────

export interface DriveItem {
  id: string;
  name: string;
  webUrl: string;
  size?: number;
  lastModifiedDateTime?: string;
  lastModifiedBy?: { user?: { displayName?: string } };
  folder?: { childCount?: number };
  file?: { mimeType?: string };
}

const ITEM_SELECT = 'id,name,webUrl,size,lastModifiedDateTime,lastModifiedBy,folder,file';

/** Children of the drive root, folders first then files (both alphabetical). */
export async function listDriveRoot(pca: IPublicClientApplication, driveId: string): Promise<DriveItem[]> {
  const data = await graphGet<{ value: DriveItem[] }>(
    pca, `/drives/${driveId}/root/children?$select=${ITEM_SELECT}&$top=200&$orderby=name`,
  );
  return sortItems(data.value ?? []);
}

/** Children of a folder within the drive. */
export async function listDriveFolder(pca: IPublicClientApplication, driveId: string, itemId: string): Promise<DriveItem[]> {
  const data = await graphGet<{ value: DriveItem[] }>(
    pca, `/drives/${driveId}/items/${itemId}/children?$select=${ITEM_SELECT}&$top=200&$orderby=name`,
  );
  return sortItems(data.value ?? []);
}

function sortItems(items: DriveItem[]): DriveItem[] {
  return [...items].sort((a, b) => {
    const af = a.folder ? 0 : 1, bf = b.folder ? 0 : 1;
    return af - bf || a.name.localeCompare(b.name);
  });
}

/**
 * Upload a file. When parentItemId is null the file lands in the drive
 * root; otherwise it lands inside that folder (item-relative path form).
 */
export async function uploadDriveFile(
  pca: IPublicClientApplication,
  driveId: string,
  parentItemId: string | null,
  fileName: string,
  body: ArrayBuffer,
  contentType: string,
): Promise<DriveItem> {
  const name = encodeURIComponent(fileName);
  const path = parentItemId
    ? `/drives/${driveId}/items/${parentItemId}:/${name}:/content`
    : `/drives/${driveId}/root:/${name}:/content`;
  return graphPut<DriveItem>(pca, path, body, contentType || 'application/octet-stream');
}

/** Delete a drive item (file or folder). Idempotent — a missing item is fine. */
export async function deleteDriveItem(
  pca: IPublicClientApplication,
  driveId: string,
  itemId: string,
): Promise<void> {
  return graphDelete(pca, `/drives/${driveId}/items/${itemId}`);
}

/**
 * Resolve (creating as needed) a nested folder path under the drive root and
 * return the deepest folder's item id. Each segment is looked up among the
 * parent's children (case-insensitive) and only created when absent, so
 * repeated calls are idempotent and never spawn "Folder 1" duplicates.
 */
export async function getOrCreateFolderPath(
  pca: IPublicClientApplication,
  driveId: string,
  segments: string[],
): Promise<string> {
  let parentId: string | null = null;
  for (const rawSeg of segments) {
    const seg = rawSeg.trim();
    if (!seg) continue;
    const children: DriveItem[] = parentId
      ? await listDriveFolder(pca, driveId, parentId)
      : await listDriveRoot(pca, driveId);
    const existing: DriveItem | undefined = children.find(c => c.folder && c.name.toLowerCase() === seg.toLowerCase());
    const folder: DriveItem = existing ?? await createDriveFolder(pca, driveId, parentId, seg);
    parentId = folder.id;
  }
  if (!parentId) throw new Error('No folder path segments provided');
  return parentId;
}

/**
 * Fetch a drive item's short-lived pre-authenticated direct download URL.
 * Used for a one-click Download (window.open) without threading an auth
 * header through an <a> tag.
 */
export async function getDownloadUrl(
  pca: IPublicClientApplication,
  driveId: string,
  itemId: string,
): Promise<string | null> {
  const data = await graphGet<{ '@microsoft.graph.downloadUrl'?: string }>(
    pca, `/drives/${driveId}/items/${itemId}?$select=id,@microsoft.graph.downloadUrl`,
  );
  return data['@microsoft.graph.downloadUrl'] ?? null;
}

/** Create a folder under the given parent (or root when parentItemId is null). */
export async function createDriveFolder(
  pca: IPublicClientApplication,
  driveId: string,
  parentItemId: string | null,
  name: string,
): Promise<DriveItem> {
  const path = parentItemId
    ? `/drives/${driveId}/items/${parentItemId}/children`
    : `/drives/${driveId}/root/children`;
  return graphPost<DriveItem>(pca, path, {
    name,
    folder: {},
    '@microsoft.graph.conflictBehavior': 'rename',
  });
}
